import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { Role } from "../../http/roles/types";
import { useUsersApi } from "../../http/users/api";
import { getRowTitle } from "../../types/utils";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import MultiCheckboxPermission from "../ui/MultiCheckboxPermission";
import MultiSelect from "../ui/MultiSelect";

export interface RoleFormBody {
  name: string;
  permissions: string[];
  departments: string[];
}

type RoleFormProps = {
  role?: Role | null;
  onSubmit: (form: RoleFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const RoleForm: FC<RoleFormProps> = ({ role, onSubmit, isDisabled, isLoading }: RoleFormProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const usersApi = useUsersApi();
  const dispatch = useContext(NotificationDispatch);

  const { data: departments, error } = usersApi.useAdminDepartments(aborter.current.signal);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error, error]);

  return (
    <Form submitText={i18n.t.common.save} disabled={isDisabled} onSubmit={() => onSubmit}>
      {({ register, formState: { errors }, setValue, watch, reset }) => {
        const permissionsSelected = watch("permissions") ?? [];
        const departmentsSelected = watch("departments") ?? [];

        useEffect(() => {
          role && reset(role);
        }, [role?.id]);

        const isCheckBoxChecked = (permission: string) => permissionsSelected.includes(permission);

        const isCheckBoxDisabled = (permissions: string[], permissionPrefix: string, permissionAction: string) => {
          return (
            !permissions.some((p) => p === permissionAction) ||
            (permissionAction === "read" &&
              permissionsSelected.some((p) => p.split(":")[0] === permissionPrefix && p !== permissionPrefix + ":read"))
          );
        };

        const onPermissionRowClick = (permissions: string[], resource: string) => {
          const rowPermissions = ["read", "insert", "update", "delete"].map((op) => `${resource}:${op}`);

          const hasAny = rowPermissions.some((perm) => permissionsSelected.includes(perm));

          if (hasAny) {
            setValue(
              "permissions",
              permissionsSelected.filter((p) => p.split(":")[0] !== resource),
            );
          } else {
            let rolesToAdd: string[] = [];
            rowPermissions.forEach((permission) => {
              !isCheckBoxDisabled(permissions, permission.split(":")[0] ?? "", permission.split(":")[1] ?? "") &&
                rolesToAdd.push(permission);
            });
            setValue("permissions", [...permissionsSelected, ...rolesToAdd]);
          }
        };

        const onCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>, permission: string) => {
          const newPermissions = e.target.checked
            ? [...permissionsSelected, permission]
            : permissionsSelected.filter((p) => p !== permission);

          if (permission.split(":")[1] !== "read") {
            !newPermissions.find((p) => permission.split(":")[0] + ":read" === p) &&
              newPermissions.push(permission.split(":")[0] + ":read");
          }

          setValue("permissions", newPermissions);
        };

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("name", {
                    required: true,
                  })}
                />
              )}
            </FormField>

            <FormField label={i18n.t.common.departments} isOptional>
              <MultiSelect
                values={
                  departments?.map((x) => ({
                    id: x,
                    bigTitle: getRowTitle(x),
                    selected: departmentsSelected?.includes(x),
                  })) ?? []
                }
                selectedItems={departmentsSelected}
                onUpdate={(id, checked) => {
                  setValue(
                    "departments",
                    checked ? [...departmentsSelected, id] : departmentsSelected?.filter((x) => x !== id),
                  );
                }}
              />
            </FormField>

            <FormField label={i18n.t.permissions.plural} isOptional>
              <MultiCheckboxPermission
                isDisabled={isDisabled || isLoading}
                onPermissionRowClick={onPermissionRowClick}
                isCheckBoxDisabled={isCheckBoxDisabled}
                isCheckBoxChecked={isCheckBoxChecked}
                onCheckBoxChange={onCheckBoxChange}
                showClients={false}
              />
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RoleForm;
